/*
 * The Meta system — hand-curated, like nonoMap.ts for /kart.
 *
 * The LIVE half of the Meta section (what is actually built in the ad account)
 * comes from GET /meta/structure. This file is the other half: what the system
 * SHOULD look like, which doors a customer comes in through, and what happened
 * from May to October. Every number here is a snapshot with a source; change it
 * by editing this file, not by guessing in the UI.
 *
 * Sources (repo-relative):
 *   PR = Nono - Brand Reset/research/pricing-2026-10/data/{quick-facts,ads-momence-match,audience}.md
 *   VoC = Brain - Nono Sync - Crm sync/05 Reference/Voice of Customer.md
 *   META = live ad account act_2608072569575700, read 19.10.2026
 */

export const BLUEPRINT_AS_OF = '19.10.2026';

/* ── Entry points: the doors a customer walks in through ─────────────────── */

export type EntryId = 'kropp' | 'livet' | 'gympress' | 'fellesskap' | 'tilbud';

export interface EntryPoint {
  id: EntryId;
  name: string;
  /** One line: the person this door is for. */
  who: string;
  /** What she is afraid of before she books. */
  fear: string;
  /** What the ad promises in her words. */
  promise: string;
  /** Measured facts behind the door (with source). */
  evidence: string[];
  /** Where the click lands. */
  landing: string;
  /** Hooks to make next (proposals, not live). */
  hooks: string[];
  /** Is the door proven, promising, or never really tested? */
  proof: 'bevist' | 'lovende' | 'utestet';
  /** Matches the ad names already in the account (lower-case regex). */
  match: RegExp;
  color: string;
}

export const ENTRY_POINTS: EntryPoint[] = [
  {
    id: 'kropp',
    name: 'Vond kropp, trygg styrke',
    who: '50–69 år med vond rygg, kne eller artrose som vil ha mindre vondt.',
    fear: '«Tåler kroppen min det, eller blir det verre?»',
    promise: 'Maskiner som er trygge for vonde ledd, og noen som passer på.',
    evidence: [
      '88 % av medlemmene nevner en plage (rygg, kne, artrose, etter operasjon). (VoC)',
      '53 % valgte Nono fordi maskinene er trygge for vonde ledd. (VoC)',
      '«Photos – Stive ledd og rygg»: 71 AED per booking, blant de billigste. (META)',
    ],
    landing: '/pages/styrketrening-med-hensyn',
    hooks: [
      'Kneet ditt trenger ikke hvile. Det trenger styrke.',
      'Vond rygg? Slik trener du uten å gjøre det verre.',
      'Fysioterapeuten sa «styrketrening». Her er hvor du starter.',
    ],
    proof: 'bevist',
    match: /(ledd|rygg|kne|vond|artrose|stiv|smerte|plage)/,
    color: '#c58b5f',
  },
  {
    id: 'livet',
    name: 'Sterk for resten av livet',
    who: '70+ som vil klare seg selv: trapper, barnebarn, bæreposer.',
    fear: '«Jeg er for gammel til å begynne nå.»',
    promise: 'Det er aldri for sent. Rolig, trygt og under 40 minutter.',
    evidence: [
      '73 % av medlemmene med kjent alder er over 60, 37 % er over 70. (PR audience)',
      'Meta valgte 55+ selv: 84 % av forbruket, uten at noe annonsesett var aldersbegrenset. (PR audience)',
      '«Dame 87 år»: 78 AED per booking. (META)',
    ],
    landing: '/pages/styrketrening-etter-50',
    hooks: [
      'Hun begynte å trene da hun var 79.',
      'Det handler ikke om å bli sprek. Det handler om å klare seg selv.',
    ],
    proof: 'bevist',
    match: /(87|70|79|eldre|livet|unge|alder|pensjon|barnebarn)/,
    color: '#8fae8b',
  },
  {
    id: 'gympress',
    name: 'Bli sterk. Uten gympress.',
    who: '30–55 som aldri har likt treningssenteret (vekstpublikummet, D-13).',
    fear: '«Alle der er så sprek. Jeg passer ikke inn.»',
    promise: 'Styrketrening satt i system. Ingen speil, ingen kropp-press, 30 minutter.',
    evidence: [
      '«Ikke enda et treningssenter» fikk klikk fra 25–44, men nesten ingen bookinger. (PR audience)',
      'Bare 7 % av medlemmene er under 50. Døren er nesten ikke testet. (PR audience)',
      'Rolig miljø uten kroppspress er grunn nr. 1 til å velge Nono: 60 %. (VoC)',
    ],
    landing: '/pages/sterk-uten-gympress',
    hooks: [
      'Du hater ikke trening. Du hater treningssenteret.',
      '30 minutter. To ganger i uka. Ingen ser på deg.',
    ],
    proof: 'utestet',
    match: /(gympress|vanlig|ikke enda|ikke et|treningssent|effektiv|30 ?min|sliten|sultne|fett)/,
    color: '#7b97c4',
  },
  {
    id: 'fellesskap',
    name: 'Folk som deg',
    who: 'Alle dørene over, når de trenger å se at «noen som meg» er der.',
    fear: '«Blir jeg alene der, uten å vite hva jeg skal gjøre?»',
    promise: 'Ekte medlemmer forteller. Vertskapet kjenner deg.',
    evidence: [
      '«Spiller på lag»: 24 bookinger til 159 AED hver. (META)',
      '«VI HAR ÅPNET»: billigste skjemalead hele perioden (31–39 AED). (META)',
      'Venner som ble vervet: 9 av 9 ble medlemmer. (PR audience)',
    ],
    landing: '/pages/provetime',
    hooks: [
      'Rønnaug (57) og Amund (27): samme system, hvert sitt nivå.',
      '«Jeg gruet meg i tre uker. Nå gleder jeg meg.»',
    ],
    proof: 'lovende',
    match: /(lag|åpnet|medlem|historie|eint av|ronnaug|rønnaug|amund|hvorfor de|fellesskap|drop in)/,
    color: '#b08cc7',
  },
  {
    id: 'tilbud',
    name: 'Tilbudet: 649 kr/mnd',
    who: 'Varme folk som allerede vurderer, og som trenger en grunn til å gjøre det nå.',
    fear: '«Det er dyrt, og jeg blir låst.»',
    promise: '649 kr/mnd · 12 mnd binding. Prøvetimen er gratis.',
    evidence: [
      'Hostkampanje 649: 41 AED per skjemalead i oktober, billigste aktive. (META)',
      'Da binding ble vist først, valgte 8 av 9 nye medlemmer binding (mot ~12 % før). (PR)',
      'Å selge på lav pris («Billig senter») brant penger i juni. (PR ads-momence-match)',
    ],
    landing: '/pages/provetime',
    hooks: ['Gratis prøvetime. Så 649 kr i måneden, hvis du vil fortsette.'],
    proof: 'lovende',
    match: /(649|pris|billig|garanti|tilbud|kampanje|bilde [a-z]|gratis)/,
    color: '#d1a54a',
  },
];

export const ENTRY_BY_ID = new Map(ENTRY_POINTS.map((e) => [e.id, e]));

/**
 * Which door an existing ad belongs to, read from its name. Checked in this
 * order, first match wins: the offer first (a «649 – 30min» ad is an offer ad,
 * not a «30 minutes» ad), then the body doors, and «Folk som deg» last because
 * people's names appear in ads that open other doors.
 */
const MATCH_ORDER: EntryId[] = ['tilbud', 'kropp', 'livet', 'gympress', 'fellesskap'];
export function entryForAd(name: string): EntryId | null {
  const n = name.toLowerCase();
  for (const id of MATCH_ORDER) if (ENTRY_BY_ID.get(id)?.match.test(n)) return id;
  return null;
}

/* ── The target structure ────────────────────────────────────────────────── */

export interface BlueprintCampaign {
  id: string;
  name: string;
  role: string;
  /** Meta objective → optimisation event, in Ads Manager words. */
  setup: string;
  budgetShare: number; // 0–1 of the daily budget
  adsets: number;
  ads: string; // e.g. "4–6"
  entries: EntryId[];
  why: string;
  /** What has to be true before this campaign is switched on. */
  precondition?: string;
}

/** Default daily budget the plan is drawn for (HANDLINGSPLAN: at least ~577 kr/day). Owner decides. */
export const DEFAULT_DAILY_NOK = 580;

export const BLUEPRINT: BlueprintCampaign[] = [
  {
    id: 'motor',
    name: 'Booking-motoren',
    role: 'Henter nye folk rett til en booket prøvetime på nettsiden.',
    setup: 'Mål: Leads (nettside) → optimaliser for Schedule (Nono-widgeten, CAPI)',
    budgetShare: 0.7,
    adsets: 1,
    ads: '4–6',
    entries: ['kropp', 'livet', 'fellesskap', 'gympress'],
    why: 'En booking på nettsiden kostet ~185 AED. Et skjemalead kostet ~55 AED, men bare 10–12 % booket, altså ~450–550 AED per booking. Widget-bookinger møter opp 90 % og blir medlem 77 %.',
  },
  {
    id: 'skjema',
    name: 'Skjema + samtale',
    role: 'Fanger dem som vil bli ringt i stedet for å booke selv.',
    setup: 'Mål: Leads (direkteskjema) → kvalitetslead, CRM-trinnene tilbake til Meta',
    budgetShare: 0.2,
    adsets: 1,
    ads: '2–3',
    entries: ['tilbud', 'kropp'],
    why: 'Skjema virker bare når noen ringer samme dag. Fra august til september booket 1 av 143 skjemalead som skulle ringes.',
    precondition: 'Vertskapet ringer hvert lead samme dag, og samtalen logges.',
  },
  {
    id: 'varm',
    name: 'Varme folk',
    role: 'Minner dem som har vært innom, men ikke booket.',
    setup: 'Mål: Leads (nettside) → Schedule, kun egne målgrupper',
    budgetShare: 0.1,
    adsets: 1,
    ads: '2–3',
    entries: ['fellesskap', 'tilbud'],
    why: '~98 % av dem som klikker booker aldri. Varme folk (besøkt siden 30 d, åpnet bookingen, så 50 % av en film, skjemalead uten booking) er billigst å vinne tilbake.',
    precondition: 'Egendefinerte målgrupper laget i Meta (nettsidebesøk, video, skjema).',
  },
];

/** The testing lane: new creative never competes with the winners for the same budget. */
export const TEST_LANE = {
  name: 'Testbanen',
  rule: 'Nye annonser testes i et eget annonsesett med fast budsjett (~100 kr/dag i 7 dager) før de flyttes inn i motoren. I en CBO-kampanje fikk de fire filmene fra 10.10 til sammen ~60 AED på en uke. De ble aldri testet.',
};

/** Rules the structure follows, with the reason in Nono's numbers. */
export const STRUCTURE_RULES: { rule: string; why: string }[] = [
  {
    rule: 'Ett annonsesett per kampanje',
    why: 'Meta trenger ~50 resultater i uka per annonsesett for å lære. Med ~580 kr/dag og ~475 kr per booking får hele kontoen ~8–9 bookinger i uka. Flere annonsesett deler et lite volum og lærer aldri.',
  },
  {
    rule: '4–6 annonser i motoren, og 1 dør per annonse',
    why: 'Meta gir pengene til 1–3 annonser uansett. Flere annonser gir bare flere som aldri får visninger (Rønnaug + Amund og Medlemshistorier fikk ~0 i oktober).',
  },
  {
    rule: 'Bred målgruppe, la kreativen velge',
    why: 'Meta fant 55+ selv. Døren i annonsen gjør aldersvalget. Frekvensen er 7–11 i Moss-radiusen, så radiusen bør vurderes utvidet.',
  },
  {
    rule: 'Mål på booking, ikke på skjema',
    why: 'Siden 28.08 har ingen aktiv kampanje optimalisert for bookinger. Meta optimaliserer for det du ber om, og vi ba om billige skjemaer.',
  },
];

/* ── Channels ────────────────────────────────────────────────────────────── */

export const CHANNELS: { name: string; verdict: 'nå' | 'fase 2' | 'ikke nå'; why: string }[] = [
  {
    name: 'Meta (Facebook + Instagram)',
    verdict: 'nå',
    why: 'Der kunden er (55+, kvinner). All målingen er bygget: Schedule via CAPI, CRM-trinn, per-annonse-inntekt.',
  },
  {
    name: 'Google Søk + Google Business Profile',
    verdict: 'fase 2',
    why: 'Små, men varme søk («treningssenter Moss», «styrketrening vond rygg»). Lavt budsjett, høy intensjon. Trenger konverteringssporing før start.',
  },
  {
    name: 'Snapchat',
    verdict: 'ikke nå',
    why: 'Kunden er ikke der: 7 % av medlemmene er under 50. Vurderes igjen hvis «Uten gympress» (30–55) viser seg å virke.',
  },
];

/* ── What happened, May → October ────────────────────────────────────────── */

export interface MonthFact {
  month: string;
  spendNok: number;
  formLeads: number;
  bookings: number; // all channels
  newMembers: number;
  partial?: boolean;
}

/** PR quick-facts.md (spend in NOK at the corrected FX). October is Meta's own number to 19.10. */
export const MONTHS: MonthFact[] = [
  { month: 'Mai', spendNok: 19376, formLeads: 177, bookings: 67, newMembers: 16 },
  { month: 'Jun', spendNok: 61705, formLeads: 109, bookings: 93, newMembers: 61 },
  { month: 'Jul', spendNok: 18000, formLeads: 0, bookings: 59, newMembers: 41 },
  { month: 'Aug', spendNok: 27481, formLeads: 59, bookings: 60, newMembers: 47 },
  { month: 'Sep', spendNok: 17299, formLeads: 138, bookings: 44, newMembers: 38 },
];

export interface HistoryEvent {
  date: string;
  title: string;
  body: string;
  tone: 'good' | 'bad' | 'neutral';
}

export const EVENTS: HistoryEvent[] = [
  {
    date: '20.05',
    title: 'Åpningskampanjen',
    body: 'Direkteskjema «VI HAR ÅPNET». 281 personer → 100 booket → 40 medlemmer. Den beste perioden for skjema: 42 % av leadene booket.',
    tone: 'good',
  },
  {
    date: '05.06',
    title: 'Booking på nettsiden',
    body: 'Schedule-kampanjen: 159 bookinger til ~185 AED hver. Juni ble toppmåneden: 61 nye medlemmer.',
    tone: 'good',
  },
  {
    date: '08–13.06',
    title: 'Bookingen gikk i stykker',
    body: 'Et falskt «Booket!»-vindu og en gammel side i Shopify-cachen. Ekte bookinger falt 11 → 0 mens klikkene var som før.',
    tone: 'bad',
  },
  {
    date: '~14.07',
    title: 'Budsjettet kuttet',
    body: 'Fra 889 til 280 kr/dag. Siden konverterte som før (1,4–1,6 %), så volumet falt på grunn av budsjettet, ikke siden.',
    tone: 'neutral',
  },
  {
    date: '16.08',
    title: 'Sannheten',
    body: 'Closing falt fra 77 % til 31 %. Churn steg fra 8 % til 21 % i måneden. Alle 64 som sluttet hadde 899 uten binding, og 35 sluttet etter én måned.',
    tone: 'bad',
  },
  {
    date: '28.08',
    title: 'Alt flyttet til skjema',
    body: 'Nettside-kampanjen stoppet. Billige skjemalead, men bookingraten falt til 10–12 %, og bare 1 av 143 som skulle ringes booket.',
    tone: 'bad',
  },
  {
    date: '22.09',
    title: '649 med binding',
    body: 'Høstkampanjen: 8 av 9 nye valgte binding da den ble vist først. Prisen ble vedtatt fra 01.11.',
    tone: 'good',
  },
  {
    date: '10.10',
    title: 'Filmene som aldri fikk sjansen',
    body: 'Fire nye videoannonser i en CBO-kampanje fikk ~60 AED til sammen på en uke. Ikke testet.',
    tone: 'bad',
  },
  {
    date: '19.10',
    title: 'Ny nettside',
    body: 'Brand Reset er live. Nå trengs et system som sender folk dit og måler bookinger.',
    tone: 'good',
  },
];

export const LESSONS: { title: string; body: string }[] = [
  {
    title: 'Kunden fant oss, ikke omvendt',
    body: 'Annonsene ble laget for 25–65, men Meta fant 55+ kvinner med vonde ledd. Det er ikke et problem. Det er posisjonen.',
  },
  {
    title: 'Vi målte på feil ting',
    body: 'Fra slutten av august ba vi Meta om billige skjemaer, og det fikk vi. Bookinger og medlemmer var det vi trengte.',
  },
  {
    title: 'Hullet var etter klikket',
    body: 'Lead → booking falt fra 42 % til 10 % og closing fra 77 % til 31 %. Ingen samtaler ble logget, så ingen så det mens det skjedde.',
  },
  {
    title: 'Prisen drev churn, ikke tilstrømning',
    body: 'Annonsene hentet medlemmer til ~1 100 kr. De forsvant fordi 899 uten binding var det eneste som ble vist.',
  },
  {
    title: 'Moss er lite',
    body: 'Frekvensen var 7–11 og CTR falt fra 6,4 % til 1,7 %. De samme 20–40 000 menneskene så de samme annonsene om og om igjen.',
  },
  {
    title: 'Strukturen sultet det nye',
    body: 'CBO ga pengene til den gamle vinneren. Nye kreativer fikk aldri en ekte test.',
  },
];
